import { BaseAPI } from '../../../../shared/infra/api/BaseAPI';
import { APIResponse } from '../../../../shared/infra/api/APIResponse';
import { IAuthService } from '../../../users/services/authService';
import { Result } from '../../../../shared/core/Result';
import { left, right } from '../../../../shared/core/Either';
import {
  AdminRequestDTO,
  BatchUserRequestDTO,
  MemberRequestDTO,
  MembersResponseDTO,
  RecentMembersResponseDTO,
} from '../dtos/Member.dto';
import { MemberModel } from '../models/Member.model';
import { MembersUtil } from '../utils/MembersUtil';
import { PaginationDataModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { PaginationUtil } from '../../../../shared/components/pagination/utils/PaginationUtils';
import { AxiosError } from 'axios';
import { UserRolesRequestDTO } from '../dtos/UserRole.dto';
import { AdministratorRequestDTO } from '../../../administrator/admin/infra/types';
import { UserVerificationRequestDTO } from '../dtos/UserVerification.dto';
import { GuestModel } from '../models/Guest.model';
import { GuestsResponseDTO } from '../dtos/Guest.dto';
import { GuestUtil } from '../utils/GuestUtil';

export interface IMembersService {
  getCommunityMembers(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
  ): Promise<APIResponse<PaginationDataModel<MemberModel[]>>>;
  getCommunityGuests(
    offset?: number,
    filter?: string,
    dir?: number,
    deleted?: boolean,
  ): Promise<APIResponse<PaginationDataModel<GuestModel[]>>>;
  deleteMembers(
    deleteMembersRequest: BatchUserRequestDTO,
  ): Promise<APIResponse<void>>;
  activateMembers(
    activateMembersRequest: BatchUserRequestDTO,
  ): Promise<APIResponse<void>>;
  deactivateMembers(
    deactivateMembersRequest: BatchUserRequestDTO,
  ): Promise<APIResponse<void>>;
  verifyUsers(
    verifyUserRequest: BatchUserRequestDTO,
  ): Promise<APIResponse<void>>;
  createCommunityAdmin(
    memberRequest: MemberRequestDTO,
  ): Promise<APIResponse<void>>;
  updateCommunityMember(
    memberRequest: MemberRequestDTO,
  ): Promise<APIResponse<void>>;
  updateMemberRole(
    roleRequest: UserRolesRequestDTO,
  ): Promise<APIResponse<void>>;
  updateMemberVerification(
    verificationRequest: UserVerificationRequestDTO,
  ): Promise<APIResponse<void>>;
  getRecentMembers(): Promise<APIResponse<MemberModel[]>>;
  createAdministrator(
    administratorRequest: AdministratorRequestDTO,
  ): Promise<APIResponse<void>>;
}

export class MembersService extends BaseAPI implements IMembersService {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onFailureExecuteImpl(error: AxiosError<any>): Promise<any> {
    throw new Error('Method not implemented.');
  }

  constructor(authService: IAuthService) {
    super(authService);
  }

  hasToken(): boolean {
    return this.authService.isUserAuthenticated();
  }

  public async createCommunityAdmin(
    adminRequest: AdminRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.post<MemberRequestDTO>({
        url: '/v1/users/community/admin',
        requiresToken: true,
        payload: adminRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async createAdministrator(
    administratorRequest: AdministratorRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.post<AdministratorRequestDTO>({
        url: '/v1/users/administrator/admin',
        requiresToken: true,
        payload: administratorRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async updateMemberRole(
    roleRequest: UserRolesRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch({
        url: `/v1/users/roles/${roleRequest.userId || ''}`,
        requiresToken: true,
        payload: { role: roleRequest.role },
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async updateMemberVerification(
    verificationRequest: UserVerificationRequestDTO,
  ): Promise<APIResponse<void>> {
    const { userId, isEmailVerified = true } = verificationRequest;
    try {
      await this.patch({
        url: `v1/users/admin-verification/${userId || ''}`,
        requiresToken: true,
        payload: { isEmailVerified: isEmailVerified },
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async updateCommunityMember(
    memberRequest: MemberRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch({
        url: `/v1/member/update/${memberRequest.id || ''}`,
        requiresToken: true,
        payload: memberRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async getCommunityMembers(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
    deleted?: boolean,
    inactive?: boolean,
  ): Promise<APIResponse<PaginationDataModel<MemberModel[]>>> {
    try {
      const response = await this.get<MembersResponseDTO>({
        url: '/v1/members',
        requiresToken: true,
        config: {
          params: { offset, filter, dir, search, inactive, deleted },
        },
      });
      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<MemberModel[]>>({
          model: data.map((member) => MembersUtil.mapToViewModel(member)),
          meta: PaginationUtil.mapToViewModel(meta),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async getCommunityGuests(
    offset?: number | undefined,
    filter?: string | undefined,
    dir?: number | undefined,
    deleted?: boolean | undefined,
  ): Promise<APIResponse<PaginationDataModel<GuestModel[]>>> {
    try {
      const response = await this.get<GuestsResponseDTO>({
        url: '/v2/guests',
        requiresToken: true,
        config: {
          params: { offset, filter, dir, deleted },
        },
      });
      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<GuestModel[]>>({
          model: data.map((item) => GuestUtil.mapToViewModel(item)),
          meta: PaginationUtil.mapToViewModel(meta),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async getRecentMembers(): Promise<APIResponse<MemberModel[]>> {
    try {
      const response = await this.get<RecentMembersResponseDTO>({
        url: '/v1/admin/analytics/members/recent',
        requiresToken: true,
      });

      const { members } = response.data;

      return right(
        Result.ok<MemberModel[]>(
          members.map((item) => MembersUtil.mapToViewModel(item)),
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async deleteMembers(
    deleteMembersRequest: BatchUserRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch({
        url: '/v1/members/batch/destroy',
        requiresToken: true,
        payload: deleteMembersRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async deactivateMembers(
    deactivateMembersRequest: BatchUserRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch({
        url: '/v1/members/batch/deactivate',
        requiresToken: true,
        payload: deactivateMembersRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async activateMembers(
    activateMembersRequest: BatchUserRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch({
        url: '/v1/members/batch/activate',
        requiresToken: true,
        payload: activateMembersRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async verifyUsers(
    verifyUserRequest: BatchUserRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch({
        url: '/v1/users/verify/batch',
        requiresToken: true,
        payload: verifyUserRequest,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }
}
